/**
 * Create a promise-aware debounced function.
 *
 * Features:
 * - latestOnly: only the most recent call gets the result; previous pending callers are rejected with DebouncedSuperseded
 * - resolveAll: all callers within the window resolve with the same final result
 * - cancel(): reject pending callers with DebouncedCanceled
 * - flush(): run immediately and resolve/reject all pending callers
 *
 * Notes:
 * - This implements trailing execution. Leading can be added if needed later.
 */
export function createDebounced(fn, wait = 0, options = {}) {
    const { latestOnly = true } = options;

    let timerId = null;
    let lastArgs = null;
    /** @type {Array<{resolve: Function, reject: Function}>} */
    let pendingCallers = [];

    function makeError(name) {
        const err = new Error(name);
        err.name = name;
        return err;
    }

    async function execute() {
        const callers = pendingCallers.slice();
        pendingCallers = [];
        timerId = null;
        try {
            const result = await fn(...lastArgs);
            callers.forEach(({ resolve }) => {
                try { resolve(result); } catch (_) {}
            });
            return result;
        } catch (error) {
            callers.forEach(({ reject }) => {
                try { reject(error); } catch (_) {}
            });
            throw error;
        }
    }

    const debounced = (...args) => {
        lastArgs = args;
        return new Promise((resolve, reject) => {
            if (latestOnly && pendingCallers.length > 0) {
                const superseded = makeError('DebouncedSuperseded');
                pendingCallers.forEach(({ reject: rj }) => {
                    try { rj(superseded); } catch (_) {}
                });
                pendingCallers = [];
            }

            pendingCallers.push({ resolve, reject });

            if (timerId) {
                clearTimeout(timerId);
            }
            timerId = setTimeout(() => {
                execute().catch(() => {});
            }, wait);
        });
    };

    debounced.cancel = () => {
        if (timerId) {
            clearTimeout(timerId);
            timerId = null;
        }
        const canceled = makeError('DebouncedCanceled');
        const callers = pendingCallers.slice();
        pendingCallers = [];
        callers.forEach(({ reject }) => {
            try { reject(canceled); } catch (_) {}
        });
    };

    debounced.flush = () => {
        if (!timerId) return Promise.resolve(undefined);
        clearTimeout(timerId);
        timerId = null;
        return execute();
    };

    return debounced;
}

export default createDebounced;
